// src/features/Profile.js
import React, { useState, useEffect } from 'react';
// Подключаем наш настроенный API клиент
import API from '../../api/axiosConfig';

function Profile() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  
  // Базовый URL для скачивания PDF (берем из .env, никаких зашитых IP)
  const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;

  useEffect(() => {
    setLoading(true);
    
    // Используем API вместо axios. Путь относительный, куки и IP подтягиваются автоматически.
    API.get('/api/history/')
    .then(res => {
      setHistory(Array.isArray(res.data) ? res.data : []);
      setLoading(false);
    })
    .catch(err => {
      console.error("Ошибка загрузки истории:", err);
      setHistory([]);
      setLoading(false);
    });
  }, []);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) return <div className="p-10 text-center font-sans text-gray-500">Загрузка истории...</div>;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 font-sans">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-xl font-black text-gray-800 mb-6 flex items-center">
          <span className="mr-2 text-2xl text-blue-600">📈</span> История тестов
        </h2>

        <div className="space-y-3">
          {history.length === 0 ? (
            <div className="bg-white p-10 rounded-2xl text-center text-gray-400 border border-dashed border-gray-200">
              Вы еще не прошли ни одного теста.
            </div>
          ) : (
            history.map((result) => (
              <div key={result.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all">
                
                <div className="p-4 flex items-center justify-between">
                  {/* ЛЕВАЯ ЧАСТЬ: Иконка, Предмет, Класс, Дата */}
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-gray-50 rounded-xl text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm md:text-base leading-tight">
                        {result.subject_name} — <span className="text-blue-600">{result.class_level} класс</span>
                      </h3>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                        {new Date(result.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>

                  {/* ПРАВАЯ ЧАСТЬ: Баллы -> Проценты -> PDF -> Кнопка Ответы */}
                  <div className="flex items-center space-x-4">
                    
                    {/* БЛОК С ЦИФРАМИ (Баллы) */}
                    <div className="text-right min-w-[70px]">
                      <div className="text-sm font-black text-gray-900 leading-none">
                        {result.score} <span className="text-gray-300">/</span> {result.total_questions}
                      </div>
                      <div className="w-full bg-gray-100 h-1.5 rounded-full mt-1.5 overflow-hidden">
                        <div 
                          className="bg-green-500 h-full transition-all duration-700" 
                          style={{ width: `${(result.score / result.total_questions) * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* ПРОЦЕНТНЫЙ БЕЙДЖ */}
                    <div className={`min-w-[45px] text-center text-[10px] font-black px-1.5 py-1 rounded-md ${
                      (result.score / result.total_questions) >= 0.8 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {Math.round((result.score / result.total_questions) * 100)}%
                    </div>

                    {/* КНОПКА PDF */}
                    {result.pdf_report && (
                      <a 
                        href={result.pdf_report.startsWith('http') ? result.pdf_report : `${baseUrl}${result.pdf_report}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-[#4A4444] hover:bg-black text-white text-[9px] font-black px-2 py-1.5 rounded-md flex items-center justify-center leading-none transition-all hover:scale-105 active:scale-95"
                        title="Скачать PDF отчет"
                      >
                        PDF
                      </a>
                    )}

                    {/* КНОПКА ОТВЕТЫ */}
                    <button 
                      onClick={() => toggleExpand(result.id)}
                      className="bg-[#3B66F5] hover:bg-blue-700 text-white text-[10px] font-black py-2.5 px-4 rounded-lg transition-all uppercase tracking-widest shadow-sm active:scale-95"
                    >
                      {expandedId === result.id ? 'Закрыть' : 'Ответы'}
                    </button>
                  </div>
                </div>

                {/* ПАНЕЛЬ ДЕТАЛЕЙ МАТРИЦЫ ОТВЕТОВ */}
                {expandedId === result.id && (
                  <div className="border-t border-gray-50 bg-gray-50/50 p-6 duration-300">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-center">
                      <div className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm">
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Общий балл</p>
                        <p className="text-xl font-black text-blue-600">{result.score}</p>
                      </div>
                      <div className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm">
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Верно</p>
                        <p className="text-xl font-black text-green-600">{result.score}</p>
                      </div>
                      <div className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm">
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Ошибки</p>
                        <p className="text-xl font-black text-red-500">{result.total_questions - result.score}</p>
                      </div>
                      <div className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm">
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Вопросов</p>
                        <p className="text-xl font-black text-gray-700">{result.total_questions}</p>
                      </div>
                    </div>

                    {/* Динамическая матрица ответов на основе данных из бэкенда */}
                    <div className="space-y-6 overflow-x-auto pb-4">
                      <div className="min-w-[500px]">
                        
                        {/* Строка правильных ответов */}
                        <div className="flex items-center mb-4 border-b border-gray-100 pb-2">
                          <span className="w-40 font-black text-[11px] text-gray-400 uppercase tracking-tighter">Правильные:</span>
                          <div className="flex space-x-3">
                            {(result.correct_answers || []).map((letter, i) => (
                              <span key={i} className="w-6 text-center font-bold text-gray-600 text-sm">{letter}</span>
                            ))}
                          </div>
                        </div>

                        {/* Строка ответов студента */}
                        <div className="flex items-center mb-4 border-b border-gray-100 pb-2">
                          <span className="w-40 font-black text-[11px] text-gray-400 uppercase tracking-tighter">Ваши ответы:</span>
                          <div className="flex space-x-3">
                            {(result.user_answers || []).map((letter, i) => (
                              <span key={i} className="w-6 text-center font-bold text-gray-800 text-sm">{letter}</span>
                            ))}
                          </div>
                        </div>

                        {/* Строка маркеров (+ или -) */}
                        <div className="flex items-center">
                          <span className="w-40 font-black text-[11px] text-gray-400 uppercase tracking-tighter">Результат:</span>
                          <div className="flex space-x-3">
                            {(result.user_answers || []).map((letter, i) => {
                              const isCorrect = letter === (result.correct_answers && result.correct_answers[i]);
                              return (
                                <span key={i} className={`w-6 text-center font-black text-lg ${isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                                  {isCorrect ? '+' : '-'}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;